import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import {
  IcoChevronDown,
  IcoChevronLeft,
  IcoFileText,
  IcoStar,
  IcoText,
} from "../../../components/icons";
import { BrandedLayout } from "../../../components/ui";
import { decodeMentionsToHtml } from "../../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import type { MimicState } from "../../../stores/mimic";
import type {
  BrandColor,
  FormResults,
  FormResultsPeriod,
  FormResultsQuestion,
  FormResultsRating,
  FormResultsTextResponse,
  User,
} from "../../../types";
import { TEXT_RESPONSES_INITIAL_LIMIT } from "../../../types";

type ResultsPageState = {
  currentUser: User;
  mimic: MimicState;
  results: FormResults;
  brandColor: BrandColor;
  hasReports: boolean;
};

export async function load(args: LoaderArgs): Promise<ResultsPageState> {
  const formId = args.params.formId ?? "";
  const periodStart = args.url.searchParams.get("period") ?? undefined;

  // Check auth first
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);
  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Get form results - will throw if not admin
  const results = await rpc.submissions.getFormResults({
    formId,
    periodStart,
  });

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find(
    (o) => o.id === results.orgId,
  );
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    results,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
  };
}

// Build lookup maps for combining data client-side
function buildLookups(results: FormResults) {
  const ratingsMap = new Map(results.ratings.map((r) => [r.questionId, r]));
  const textResponsesMap = new Map<string, FormResultsTextResponse[]>();
  for (const r of results.textResponses) {
    const list = textResponsesMap.get(r.questionId) ?? [];
    list.push(r);
    textResponsesMap.set(r.questionId, list);
  }
  return { ratingsMap, textResponsesMap };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle(`${state.value.results.form.title} Results`);
  const { currentUser, mimic, results, brandColor, hasReports } = state.value;
  const { form, orgId, orgName, period, periods, questions, submissionCount } =
    results;
  const { ratingsMap, textResponsesMap } = buildLookups(results);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="forms"
      isAdmin={true}
      hasReports={hasReports}
    >
      <div class="flex justify-center">
        <div class="w-full max-w-2xl flex flex-col gap-6">
          {/* Back link */}
          <a
            href={`/orgs/${orgId}/forms/${form.id}/edit`}
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back to Form
          </a>

          {/* Header */}
          <div class="flex flex-col gap-1">
            <h1 class="text-2xl font-bold text-text">
              {form.title || "Untitled form"}
            </h1>
            <p class="text-sm text-text-muted">{orgName} - Results</p>
          </div>

          {/* Period selector and summary */}
          <div class="flex items-center justify-between">
            <PeriodSelector
              currentPeriod={period}
              periods={periods}
              formId={form.id}
              orgId={orgId}
            />
            <span class="text-sm text-text-muted">
              {submissionCount}{" "}
              {submissionCount === 1 ? "response" : "responses"}
            </span>
          </div>

          {/* Results content */}
          {submissionCount === 0 ? (
            <EmptyState hasQuestions={questions.length > 0} />
          ) : (
            <div class="flex flex-col gap-4">
              {questions.map((question) => (
                <QuestionResultCard
                  key={question.id}
                  question={question}
                  rating={ratingsMap.get(question.id)}
                  textResponses={textResponsesMap.get(question.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </BrandedLayout>
  );
}

// ============================================================================
// Period Selector
// ============================================================================

function PeriodSelector({
  currentPeriod,
  periods,
  formId,
  orgId,
}: {
  currentPeriod: FormResultsPeriod | null;
  periods: FormResultsPeriod[];
  formId: string;
  orgId: string;
}) {
  if (periods.length === 0) {
    return <span class="text-sm text-text-muted">No submissions yet</span>;
  }

  if (periods.length === 1) {
    return (
      <span class="text-sm font-medium text-text">
        {formatPeriod(currentPeriod)}
      </span>
    );
  }

  const handleChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const periodStart = target.value;
    navigateTo(`/orgs/${orgId}/forms/${formId}/results?period=${periodStart}`);
  };

  // Helper to safely get ISO string from date (handles both Date objects and serialized strings)
  const toISOString = (date: Date | string) =>
    typeof date === "string" ? date : date.toISOString();

  return (
    <div class="relative inline-flex items-center">
      <select
        value={currentPeriod ? toISOString(currentPeriod.start) : ""}
        onChange={handleChange}
        class="appearance-none bg-surface border border-border rounded-lg pl-3 pr-8 py-1.5 text-sm font-medium text-text focus:outline-none focus:ring-2 focus:ring-text-subtle cursor-pointer"
      >
        {periods.map((p) => (
          <option key={toISOString(p.start)} value={toISOString(p.start)}>
            {formatPeriod(p)}
          </option>
        ))}
      </select>
      <IcoChevronDown class="absolute right-2 size-4 text-text-muted pointer-events-none" />
    </div>
  );
}

function formatPeriod(period: FormResultsPeriod | null): string {
  if (!period) return "No period";

  const startDate = new Date(period.start);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };

  if (period.end) {
    const endDate = new Date(period.end);
    return `${startDate.toLocaleDateString("en-US", options)} - ${endDate.toLocaleDateString("en-US", options)}`;
  }

  return startDate.toLocaleDateString("en-US", options);
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState({ hasQuestions }: { hasQuestions: boolean }) {
  return (
    <div class="bg-surface border border-border rounded-lg p-8 text-center">
      <IcoFileText class="size-12 mx-auto text-text-subtle mb-4" />
      <p class="text-text-muted">
        {hasQuestions
          ? "No submissions for this period yet."
          : "This form has no questions yet."}
      </p>
    </div>
  );
}

// ============================================================================
// Question Result Card
// ============================================================================

function QuestionResultCard({
  question,
  rating,
  textResponses,
}: {
  question: FormResultsQuestion;
  rating?: FormResultsRating;
  textResponses?: FormResultsTextResponse[];
}) {
  const responseCount = rating?.responseCount ?? textResponses?.length ?? 0;

  return (
    <div class="bg-surface border border-border rounded-xl p-4 flex flex-col gap-3">
      {/* Question header */}
      <div class="flex items-start gap-3">
        <div class="size-8 rounded-lg flex items-center justify-center bg-surface-hover flex-shrink-0">
          {question.type === "rating" ? (
            <IcoStar class="size-4 text-text-muted" />
          ) : (
            <IcoText class="size-4 text-text-muted" />
          )}
        </div>
        <div class="flex-1 min-w-0">
          {question.title ? (
            <h3
              class="font-medium text-text [&>*]:inline"
              dangerouslySetInnerHTML={{ __html: question.title }}
            />
          ) : (
            <h3 class="font-medium text-text-muted">Untitled question</h3>
          )}
          <p class="text-sm text-text-muted">
            {responseCount} {responseCount === 1 ? "response" : "responses"}
          </p>
        </div>
      </div>

      {/* Result content */}
      {responseCount > 0 && (
        <div class="pl-11">
          {question.type === "rating" && rating ? (
            <RatingResult
              averageRating={rating.averageRating}
              ratingMax={question.ratingMax}
            />
          ) : textResponses ? (
            <TextResponsesList responses={textResponses} />
          ) : null}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Rating Result
// ============================================================================

function RatingResult({
  averageRating,
  ratingMax,
}: {
  averageRating: number;
  ratingMax: number;
}) {
  const boxes = Array.from({ length: ratingMax }, (_, i) => {
    const boxValue = i + 1;
    const fillPercent =
      averageRating >= boxValue
        ? 100
        : averageRating > boxValue - 1
          ? (averageRating - (boxValue - 1)) * 100
          : 0;
    return { value: boxValue, fillPercent };
  });

  return (
    <div class="flex items-center gap-3">
      <div class="flex gap-1">
        {boxes.map((box) => (
          <div
            key={box.value}
            class="relative size-7 rounded bg-surface-hover text-xs font-medium flex items-center justify-center overflow-hidden"
          >
            {/* Fill layer */}
            <div
              class="absolute inset-0 bg-blue-500 dark:bg-blue-400"
              style={{ width: `${box.fillPercent}%` }}
            />
            {/* Number */}
            <span class="relative z-10 text-text-muted mix-blend-difference">
              {box.value}
            </span>
          </div>
        ))}
      </div>
      <span class="text-sm font-medium text-text">
        {averageRating.toFixed(1)}
      </span>
    </div>
  );
}

// ============================================================================
// Text Responses List
// ============================================================================

function TextResponsesList({
  responses,
}: {
  responses: FormResultsTextResponse[];
}) {
  const showAll = useSignal(false);
  const hasMore = responses.length > TEXT_RESPONSES_INITIAL_LIMIT;
  const visibleResponses = showAll.value
    ? responses
    : responses.slice(0, TEXT_RESPONSES_INITIAL_LIMIT);
  const hiddenCount = responses.length - TEXT_RESPONSES_INITIAL_LIMIT;

  if (responses.length === 0) {
    return <p class="text-sm text-text-muted italic">No text responses</p>;
  }

  return (
    <div class="flex flex-col gap-3">
      {visibleResponses.map((response) => (
        <TextResponseItem
          key={`${response.memberName}-${new Date(response.submittedAt).getTime()}`}
          response={response}
        />
      ))}

      {hasMore && !showAll.value && (
        <button
          type="button"
          onClick={() => {
            showAll.value = true;
          }}
          class="text-sm font-medium text-text-secondary hover:text-text transition-colors text-left"
        >
          Show {hiddenCount} more {hiddenCount === 1 ? "response" : "responses"}
        </button>
      )}
    </div>
  );
}

function TextResponseItem({ response }: { response: FormResultsTextResponse }) {
  const submittedDate = new Date(response.submittedAt);
  const formattedDate = submittedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const formattedTime = submittedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  const initials = (response.memberName ?? "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div class="bg-surface-hover rounded-lg p-3">
      <div class="flex items-center gap-2.5 mb-1.5">
        {response.profilePictureUrl ? (
          <img
            src={response.profilePictureUrl}
            alt=""
            class="size-6 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div class="size-6 rounded-full bg-surface-active flex items-center justify-center flex-shrink-0">
            <span class="text-[10px] font-semibold text-text-muted">
              {initials}
            </span>
          </div>
        )}
        <span class="text-sm font-semibold text-text flex-1 min-w-0">
          {response.memberName}
        </span>
        <span class="text-xs text-text-subtle whitespace-nowrap">
          {formattedDate} at {formattedTime}
        </span>
      </div>
      <div
        class="text-sm text-text prose prose-sm dark:prose-invert max-w-none pl-[34px]"
        dangerouslySetInnerHTML={{
          __html: decodeMentionsToHtml(response.text),
        }}
      />
    </div>
  );
}
